import React from 'react';
import type { FulltimePayroll } from '../PayrollTypes';
import { minutesToHoursMinutes } from '../PayrollTypes';
import PayrollOverrideCell from './PayrollOverrideCell';

interface FulltimePayrollTableProps {
  data: FulltimePayroll[];
  autoValues: Map<string, Map<string, number>>; // employeeId -> fieldName -> autoValue
  overrides: Map<string, Map<string, number>>; // employeeId -> fieldName -> overrideValue
  onOverrideChange: (employeeId: string, fieldName: string, value: number | null) => void;
  benefitRuleEnabled?: boolean;
}

const FulltimePayrollTable: React.FC<FulltimePayrollTableProps> = ({
  data,
  autoValues,
  overrides,
  onOverrideChange,
  benefitRuleEnabled = false,
}) => {
  if (data.length === 0) {
    return <p className="text-gray-500 text-sm py-4 text-center">該当する正職員がいません</p>;
  }

  const getAutoValue = (employeeId: string, fieldName: string): number => {
    return autoValues.get(employeeId)?.get(fieldName) ?? 0;
  };

  const isOverridden = (employeeId: string, fieldName: string): boolean => {
    return overrides.get(employeeId)?.has(fieldName) ?? false;
  };

  return (
    <div className="max-h-[calc(100dvh-18rem)] overflow-auto overscroll-contain">
      <table className="w-full text-sm border-collapse">
        <thead className="sticky top-0 z-20 bg-gray-50 shadow-sm">
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-3 py-2 text-left font-medium text-gray-700 sticky left-0 bg-gray-50 z-10">氏名</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">出勤日数</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-sky-50/50">休出日数</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-sky-50/50">休出時間</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">有給日数</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">欠勤日数</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">遅刻早退</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">勤務時間</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">普残時間</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">夜勤~4回</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">5回目~</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">夜勤合計</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700">出勤回数</th>
            {!benefitRuleEnabled && (
              <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">特別給付金</th>
            )}
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">特別手当</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">交通費</th>
            <th className="px-3 py-2 text-right font-medium text-gray-700 bg-amber-50/50">控除食事代</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.employeeId} className="border-b border-gray-100 hover:bg-gray-50/50">
              <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 bg-white z-10 whitespace-nowrap">{row.employeeName}</td>
              <td className="px-3 py-2 text-right text-gray-900">{row.attendanceDays}</td>
              <td className="px-3 py-2 text-right text-gray-900 bg-sky-50/30">{row.holidayWorkDays}</td>
              <td className="px-3 py-2 text-right text-gray-900 bg-sky-50/30">{minutesToHoursMinutes(row.holidayWorkMinutes)}</td>
              <td className="px-3 py-2 text-right text-gray-900">{row.paidLeaveDays}</td>
              <td className="px-1 py-1">
                <PayrollOverrideCell
                  value={row.absenceDays}
                  autoValue={getAutoValue(row.employeeId, 'absence_days')}
                  isOverridden={isOverridden(row.employeeId, 'absence_days')}
                  format="number"
                  onChange={(v) => onOverrideChange(row.employeeId, 'absence_days', v)}
                />
              </td>
              <td className="px-1 py-1">
                <PayrollOverrideCell
                  value={row.lateEarlyMinutes}
                  autoValue={getAutoValue(row.employeeId, 'late_early_minutes')}
                  isOverridden={isOverridden(row.employeeId, 'late_early_minutes')}
                  format="minutes"
                  onChange={(v) => onOverrideChange(row.employeeId, 'late_early_minutes', v)}
                />
              </td>
              <td className="px-3 py-2 text-right text-gray-900">{minutesToHoursMinutes(row.totalWorkMinutes - row.overtimeMinutes)}</td>
              <td className="px-3 py-2 text-right text-gray-900">{minutesToHoursMinutes(row.overtimeMinutes)}</td>
              <td className="px-3 py-2 text-right text-gray-900">{row.nightShiftUpTo4}</td>
              <td className="px-3 py-2 text-right text-gray-900">{row.nightShiftOver4}</td>
              <td className="px-3 py-2 text-right text-gray-900 font-medium">{row.nightShiftTotal}</td>
              <td className="px-3 py-2 text-right text-gray-900">{row.attendanceCount}</td>
              {!benefitRuleEnabled && (
                <td className="px-1 py-1">
                  <PayrollOverrideCell
                    value={row.specialBenefit}
                    autoValue={getAutoValue(row.employeeId, 'special_benefit')}
                    isOverridden={isOverridden(row.employeeId, 'special_benefit')}
                    format="currency"
                    onChange={(v) => onOverrideChange(row.employeeId, 'special_benefit', v)}
                  />
                </td>
              )}
              <td className="px-1 py-1">
                <PayrollOverrideCell
                  value={row.specialAllowance}
                  autoValue={getAutoValue(row.employeeId, 'special_allowance')}
                  isOverridden={isOverridden(row.employeeId, 'special_allowance')}
                  format="currency"
                  onChange={(v) => onOverrideChange(row.employeeId, 'special_allowance', v)}
                />
              </td>
              <td className="px-1 py-1">
                <PayrollOverrideCell
                  value={row.transportAllowance}
                  autoValue={getAutoValue(row.employeeId, 'transport_allowance')}
                  isOverridden={isOverridden(row.employeeId, 'transport_allowance')}
                  format="currency"
                  onChange={(v) => onOverrideChange(row.employeeId, 'transport_allowance', v)}
                />
              </td>
              <td className="px-1 py-1">
                <PayrollOverrideCell
                  value={row.mealDeduction}
                  autoValue={getAutoValue(row.employeeId, 'meal_deduction')}
                  isOverridden={isOverridden(row.employeeId, 'meal_deduction')}
                  format="currency"
                  onChange={(v) => onOverrideChange(row.employeeId, 'meal_deduction', v)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FulltimePayrollTable;
